import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { listCodeChanges, createCodeChange } from "../lib/adminPortal";
import { api } from "../lib/api";
import { Plus, X, ChevronRight } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import ListTable, { ListRow } from "../components/ListTable";

const PERIODS = [
  { v: "all", label: "All" },
  { v: "30d", label: "Last 30 days" },
  { v: "quarter", label: "Last quarter" },
  { v: "ytd", label: "Last year" },
];

export default function AdminCodeChanges() {
  const { user } = useRole();
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [showRecord, setShowRecord] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    listCodeChanges(user.id, { period }).then((d) => alive && setData(d));
    return () => {
      alive = false;
    };
  }, [user?.id, period, refreshTick]);

  return (
    <section className="animate-fade-in-up" data-testid="admin-code-changes-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>Platform Admin · Code Changes</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Code-Change Register.
          </h1>
          <p style={{ color: "#6C6B85", fontSize: 13, marginTop: 6, maxWidth: 720, lineHeight: 1.55 }}>
            Zoning and building-code changes affecting designs in the platform's catalog.
            Architects of record are expected to confirm compliance within one month of any
            change here.
          </p>
        </div>
        <button
          className="wn-btn wn-btn-primary"
          onClick={() => setShowRecord(true)}
          data-testid="admin-record-change-btn"
        >
          <Plus size={14} /> Record a code change
        </button>
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="admin-cc-filters">
        {PERIODS.map((p) => {
          const active = period === p.v;
          return (
            <button
              key={p.v}
              onClick={() => setPeriod(p.v)}
              data-testid={`admin-cc-filter-${p.v}`}
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
                background: active ? "#1B1F4B" : "transparent",
                color: active ? "#FBFAFD" : "#272636",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 16 }} />

      <ListTable
        testId="admin-cc"
        columns={[
          { label: "Code change" },
          { label: "Effective" },
          { label: "Affected designs", align: "right" },
        ]}
        gridTemplate={CC_GRID}
        gap={CC_GAP}
        items={data?.items}
        loadingRows={2}
        loadingHeight={56}
        emptyTitle="No code changes recorded yet."
        emptyBody={
          <button
            className="wn-btn wn-btn-tertiary mt-3"
            style={{ paddingLeft: 0 }}
            onClick={() => setShowRecord(true)}
          >
            Record the first one <ChevronRight size={14} />
          </button>
        }
        renderRow={(cc, _i, isLast) => (
          <CodeRow key={cc.id} cc={cc} isLast={isLast} />
        )}
      />

      {showRecord && (
        <RecordChangeDrawer
          userId={user.id}
          onClose={() => setShowRecord(false)}
          onSuccess={() => {
            setShowRecord(false);
            setRefreshTick((t) => t + 1);
          }}
        />
      )}
    </section>
  );
}

/* Shared column template */
const CC_GRID = "minmax(0, 2.5fr) 160px 140px";
const CC_GAP = 18;

function CodeRow({ cc, isLast }) {
  const affected = (cc.affected_design_ids || []).length;
  return (
    <ListRow
      as={Link}
      to={`/admin/code-changes/${cc.id}`}
      gridTemplate={CC_GRID}
      gap={CC_GAP}
      isLast={isLast}
      data-testid={`admin-cc-row-${cc.id}`}
    >
      <div className="min-w-0">
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 15,
            color: "#1B1F4B",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {cc.summary}
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 11.5,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Recorded by {cc.recorded_by} · {relativeDate(cc.recorded_at)}
        </div>
      </div>
      <span
        className="tabular-nums"
        style={{
          color: "#272636",
          fontWeight: 600,
          fontSize: 12.5,
          whiteSpace: "nowrap",
        }}
      >
        {cc.effective_date}
      </span>
      <span
        className="tabular-nums"
        style={{
          color: affected > 0 ? "#1B1F4B" : "#9AA0C0",
          fontWeight: 700,
          fontSize: 13,
          textAlign: "right",
        }}
      >
        {affected}
      </span>
    </ListRow>
  );
}

function RecordChangeDrawer({ userId, onClose, onSuccess }) {
  const [summary, setSummary] = useState("");
  const [effective, setEffective] = useState("");
  const [notes, setNotes] = useState("");
  const [designs, setDesigns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/marketplace/designs").then((r) => {
      // /marketplace/designs returns a paginated shape sometimes — be defensive
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setDesigns(list);
    });
  }, []);

  const validSummary = summary.trim().length >= 20 && summary.trim().length <= 200;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(effective);

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      await createCodeChange(userId, {
        summary: summary.trim(),
        effective_date: effective,
        notes: notes.trim(),
        affected_design_ids: selected,
      });
      onSuccess();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not record this change.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(27,31,75,0.45)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 50,
      }}
      data-testid="admin-cc-drawer"
    >
      <div
        style={{
          width: "min(560px, 100%)",
          height: "100%",
          background: "#FBFAFD",
          padding: 24,
          overflowY: "auto",
        }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between">
          <h2
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Record a code change.
          </h2>
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={onClose}
            aria-label="Close"
            data-testid="admin-cc-drawer-close"
          >
            <X size={16} />
          </button>
        </div>

        <label className="wn-label mt-5 block">Summary (20–200 chars)</label>
        <textarea
          className="wn-input"
          style={{ minHeight: 90, resize: "vertical" }}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder='e.g. "Updated IRC 2024 fire egress requirements for ADUs under 750 sqft."'
          data-testid="admin-cc-summary"
        />
        <div
          className="tabular-nums"
          style={{
            fontSize: 11,
            color: validSummary ? "#6C6B85" : "#A23B2E",
            marginTop: 2,
          }}
        >
          {summary.trim().length} / 200 characters · min 20
        </div>

        <label className="wn-label mt-4 block">Effective date</label>
        <input
          type="date"
          className="wn-input tabular-nums"
          value={effective}
          onChange={(e) => setEffective(e.target.value)}
          data-testid="admin-cc-effective"
        />

        <label className="wn-label mt-4 block">Affected designs (optional)</label>
        <div
          className="wn-card"
          style={{
            padding: 8,
            maxHeight: 220,
            overflowY: "auto",
          }}
          data-testid="admin-cc-designs"
        >
          {designs.length === 0 && (
            <div style={{ color: "#6C6B85", fontSize: 12, padding: 6 }}>Loading designs…</div>
          )}
          {designs.map((d) => {
            const checked = selected.includes(d.id);
            return (
              <label
                key={d.id}
                className="flex items-start gap-2 cursor-pointer"
                style={{ padding: "6px 8px", borderRadius: 4 }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked
                        ? [...prev, d.id]
                        : prev.filter((x) => x !== d.id),
                    )
                  }
                  style={{ marginTop: 3 }}
                  data-testid={`admin-cc-design-${d.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div
                    style={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#1B1F4B",
                    }}
                  >
                    {d.name}
                  </div>
                  <div
                    className="tabular-nums"
                    style={{ color: "#6C6B85", fontSize: 11 }}
                  >
                    {d.architect_firm || "—"}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <label className="wn-label mt-4 block">Notes (optional)</label>
        <textarea
          className="wn-input"
          style={{ minHeight: 80, resize: "vertical" }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="admin-cc-notes"
        />

        {err && (
          <div className="mt-3" style={{ color: "#A23B2E", fontSize: 13 }} data-testid="admin-cc-error">
            {err}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button className="wn-btn wn-btn-tertiary" onClick={onClose}>Cancel</button>
          <button
            className="wn-btn wn-btn-primary"
            onClick={submit}
            disabled={busy || !validSummary || !validDate}
            data-testid="admin-cc-submit"
          >
            {busy ? "Recording…" : "Record change"}
          </button>
        </div>
      </div>
    </div>
  );
}
